import React, { memo, useCallback, useMemo, type ComponentType } from 'react';
import pick from 'lodash/pick';
import { graphql, useFragment } from 'react-relay';
import { useInlineCreateHandlers } from '@atlassian/jira-issue-table-inline-issue-create/src/ui/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsItemExpanded } from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import {
	getChildQueryVariablesForJql,
	type Column,
} from '@atlassian/jira-issue-table-hierarchy/src/common/utils/get-child-query-variables/index.tsx';
import type {
	RenderChildRows,
	RenderRows,
	RenderCreateIssueController,
	RenderDraggableWrapper,
} from '@atlassian/jira-issue-table-hierarchy/src/ui/types.tsx';

import {
	ASSIGNEE_TYPE,
	CATEGORY_TYPE,
	PRIORITY_TYPE,
	NUMBER_CF_TYPE,
	SPRINT_TYPE,
	STATUS_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
	LABELS_TYPE,
	MULTI_GROUP_CF_TYPE,
	RADIO_BUTTONS_CF_TYPE,
	MULTI_SELECT_CF_TYPE,
	MULTI_CHECKBOXES_CF_TYPE,
	SELECT_CF_TYPE,
	MULTI_VERSION_CF_TYPE,
	AFFECTS_VERSIONS_TYPE,
	FIX_VERSIONS_TYPE,
	REPORTER_TYPE,
	USER_CF_TYPE,
	LABELS_CF_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { GroupContextFieldOptimisticResponse } from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/optimistic-responses/types.tsx';
import type { GroupContextField } from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/types.tsx';
import {
	isOptionFieldType,
	isGroupPickerFieldType,
	isUserFieldType,
	isVersionFieldType,
	isLabelFieldType,
} from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/index.tsx';
import type { Props as ChildIssueCreateRowProps } from '@atlassian/jira-issue-table-hierarchy/src/ui/child-issue-create-row/index.tsx';
import type { FirstGroupRow_rankField$key } from './__generated__/FirstGroupRow_rankField.graphql';
import type {
	FirstGroupRow_GroupRow$data,
	FirstGroupRow_GroupRow$key,
} from './__generated__/FirstGroupRow_GroupRow.graphql';
import type { FirstGroupRow$key } from './__generated__/FirstGroupRow.graphql';
import { FirstGroupIssues } from './first-group-issues/FirstGroupIssues.tsx';
import { useInitialiseFirstGroupIssues } from './first-group-issues/utils.tsx';
import { GroupHeader } from './group-header/GroupHeader.tsx';

export type BaseProps = {
	groupConnectionId: string;
	isVirtual: boolean;
	isGroupAtTop?: boolean;
	projectKey?: string | null;
	columns: Column[];
	renderChildRows?: RenderChildRows;
	isIssueCreateEnabled?: boolean;
	isGridNavigationEnabled?: boolean;
	isStickyColumnsVisible?: boolean;
	isAutoUnstickyOnSmallScreenEnabled?: boolean;
};

type FirstGroupRowProps = BaseProps & {
	firstGroupRow: FirstGroupRow$key;
	rankField: FirstGroupRow_rankField$key | null;
	loading: boolean;
	childItemsExperience: string;
	childItemsLoadMoreExperience: string;
	analyticsSource: string;
	pageSize: number;
	width: number;
	isIssueRankDescending: boolean;
	isHideDoneItemsEnabled: boolean;
	isTimelineEnabled: boolean;
	actionColumns: string[];
	amountOfColumns: number;
	renderRows: RenderRows;
	renderCreateIssueController: RenderCreateIssueController;
	renderDraggableWrapper?: RenderDraggableWrapper;
	isConditionalFormattingEnabled: boolean;
	isInlineFieldConfigEnabled: boolean;
	ChildIssueCreateRowComponent?: ComponentType<ChildIssueCreateRowProps> | null;
	isLinkRelationshipFieldEnabled: boolean;
	isRemoteLinkedIssuesEnabled: boolean;
	isEditableSyncLoaded: boolean;
	isInlineEditingEnabled: boolean;
};

const toGroupContextFieldInput = ({
	fieldId,
	fieldValue,
	groupId,
	fieldType,
	fieldSetId,
}: {
	fieldId: string;
	fieldValue: FirstGroupRow_GroupRow$data['fieldValue'];
	groupId: string;
	fieldType: string;
	fieldSetId?: string | null;
}): GroupContextField | undefined => {
	const groupFieldValueBase = {
		fieldId,
		groupId,
		optimisticResponse: fieldSetId
			? getGroupContextFieldOptimisticResponse({
					fieldType,
					fieldSetId,
					fieldValue,
				})
			: undefined,
	};

	switch (fieldValue?.__typename) {
		case 'JiraJqlPriorityFieldValue':
			return {
				...groupFieldValueBase,
				fieldType: PRIORITY_TYPE,
				fieldValue: fieldValue?.priority?.priorityId ?? null,
			};
		case 'JiraJqlUserFieldValue':
			return isUserFieldType(fieldType)
				? {
						...groupFieldValueBase,
						fieldType,
						fieldValue: fieldValue?.user?.accountId ?? null,
					}
				: undefined;
		case 'JiraJqlStatusFieldValue':
			return {
				...groupFieldValueBase,
				fieldType: STATUS_TYPE,
				fieldValue: fieldValue?.status?.id ?? null,
			};
		case 'JiraJqlNumberFieldValue':
			return {
				...groupFieldValueBase,
				fieldType: NUMBER_CF_TYPE,
				fieldValue: fieldValue?.number ?? null,
			};
		case 'JiraJqlOptionFieldValue':
			return isOptionFieldType(fieldType)
				? {
						...groupFieldValueBase,
						fieldType,
						fieldValue: fieldValue?.option?.optionId ?? null,
					}
				: undefined;
		case 'JiraJqlSprintFieldValue':
			return {
				...groupFieldValueBase,
				fieldType: SPRINT_TYPE,
				fieldValue: fieldValue?.sprint?.sprintId ?? null,
			};
		case 'JiraJqlLabelFieldValue':
			return isLabelFieldType(fieldType)
				? {
						...groupFieldValueBase,
						fieldType,
						fieldValue: fieldValue?.label?.name ?? null,
					}
				: undefined;
		case 'JiraJqlGroupFieldValue':
			return isGroupPickerFieldType(fieldType)
				? {
						...groupFieldValueBase,
						fieldType,
						fieldValue: fieldValue?.group?.name ?? null,
					}
				: undefined;
		case 'JiraJqlVersionFieldValue':
			return isVersionFieldType(fieldType)
				? {
						...groupFieldValueBase,
						fieldType,
						fieldValue: fieldValue?.version?.versionId ?? null,
					}
				: undefined;
		default:
			return undefined;
	}
};

export const getGroupContextFieldOptimisticResponse = ({
	fieldType,
	fieldSetId,
	fieldValue,
}: {
	fieldType: string;
	fieldValue: FirstGroupRow_GroupRow$data['fieldValue'];
	fieldSetId?: string | null;
}): GroupContextFieldOptimisticResponse | undefined => {
	if (!fieldSetId) {
		return undefined;
	}

	switch (fieldType) {
		case PRIORITY_TYPE:
			if (fieldValue?.__typename === 'JiraJqlPriorityFieldValue' && fieldValue?.priority) {
				return {
					type: PRIORITY_TYPE,
					fieldSetId,
					priority: {
						id: fieldValue.priority.priorityId,
						name: fieldValue.priority.name,
						iconUrl: fieldValue.priority.iconUrl,
					},
				};
			}
			break;
		case ASSIGNEE_TYPE:
		case REPORTER_TYPE:
		case USER_CF_TYPE:
			return {
				type: fieldType,
				fieldSetId,
				user:
					fieldValue?.__typename === 'JiraJqlUserFieldValue' && fieldValue?.user
						? {
								id: fieldValue.user.accountId,
								accountId: fieldValue.user.accountId,
								name: fieldValue.user.name,
								picture: fieldValue.user.picture,
							}
						: null,
			};
		case STATUS_TYPE:
			if (
				fieldValue?.__typename === 'JiraJqlStatusFieldValue' &&
				fieldValue?.status &&
				fieldValue?.status.statusCategory
			) {
				return {
					type: STATUS_TYPE,
					fieldSetId,
					status: {
						id: fieldValue.status.id,
						name: fieldValue.status.name,
						statusId: fieldValue.status.id,
						statusCategory: {
							statusCategoryId: fieldValue.status.statusCategory.statusCategoryId,
							id: fieldValue.status.statusCategory.id,
						},
					},
				};
			}
			break;
		case SELECT_CF_TYPE:
		case MULTI_SELECT_CF_TYPE:
		case MULTI_CHECKBOXES_CF_TYPE:
		case RADIO_BUTTONS_CF_TYPE:
		case CATEGORY_TYPE:
			return {
				type: fieldType,
				fieldSetId,
				option:
					fieldValue?.__typename === 'JiraJqlOptionFieldValue' && fieldValue?.option
						? {
								id: fieldValue.option.optionId,
								value: fieldValue.option.value,
								optionId: fieldValue.option.optionId,
								color: fieldValue.option.color
									? {
											colorKey: fieldValue.option.color.colorKey,
											id: fieldValue.option.color.id,
										}
									: undefined,
							}
						: null,
			};
		case SPRINT_TYPE:
			return {
				type: SPRINT_TYPE,
				fieldSetId,
				sprint:
					fieldValue?.__typename === 'JiraJqlSprintFieldValue' && fieldValue?.sprint
						? {
								id: fieldValue.sprint.sprintId,
								sprintId: fieldValue.sprint.sprintId,
								name: fieldValue.sprint.name,
								state: fieldValue.sprint.state,
								endDate: fieldValue.sprint.endDate,
							}
						: null,
			};
		case STORY_POINT_ESTIMATE_CF_TYPE:
			return {
				type: NUMBER_CF_TYPE,
				value: fieldValue?.__typename === 'JiraJqlNumberFieldValue' ? fieldValue.number : null,
				fieldSetId,
			};
		case NUMBER_CF_TYPE:
			return {
				type: NUMBER_CF_TYPE,
				value: fieldValue?.__typename === 'JiraJqlNumberFieldValue' ? fieldValue.number : null,
				fieldSetId,
				isStoryPointField: true,
			};
		case LABELS_TYPE:
		case LABELS_CF_TYPE:
			return {
				type: fg('group_by_any_field') ? fieldType : LABELS_TYPE,
				value:
					fieldValue?.__typename === 'JiraJqlLabelFieldValue'
						? (fieldValue.label?.name ?? null)
						: null,
				fieldSetId,
			};
		case MULTI_GROUP_CF_TYPE:
			return {
				type: MULTI_GROUP_CF_TYPE,
				value:
					fieldValue?.__typename === 'JiraJqlGroupFieldValue' && fieldValue?.group
						? {
								id: fieldValue.group.id,
								groupId: fieldValue.group.groupId,
								name: fieldValue.group.name,
							}
						: null,
				fieldSetId,
			};
		case MULTI_VERSION_CF_TYPE:
		case AFFECTS_VERSIONS_TYPE:
		case FIX_VERSIONS_TYPE:
			return {
				type: fieldType,
				fieldSetId,
				version:
					fieldValue?.__typename === 'JiraJqlVersionFieldValue' && fieldValue?.version
						? {
								id: fieldValue.version.id,
								name: fieldValue.version.name ?? '',
								versionId: fieldValue.version.versionId,
							}
						: null,
			};
		default:
			return undefined;
	}
};

export const FirstGroupRow = memo(
	({
		firstGroupRow,
		rankField,
		groupConnectionId,
		isVirtual,
		loading,
		isGroupAtTop,
		projectKey = null,
		columns,
		actionColumns,
		amountOfColumns,
		isHideDoneItemsEnabled,
		isTimelineEnabled,
		pageSize,
		childItemsExperience,
		childItemsLoadMoreExperience,
		analyticsSource,
		width,
		isIssueRankDescending,
		isIssueCreateEnabled,
		isGridNavigationEnabled,
		renderRows,
		renderCreateIssueController,
		renderDraggableWrapper,
		isConditionalFormattingEnabled,
		isInlineFieldConfigEnabled,
		isStickyColumnsVisible,
		isAutoUnstickyOnSmallScreenEnabled,
		ChildIssueCreateRowComponent,
		isLinkRelationshipFieldEnabled,
		isRemoteLinkedIssuesEnabled,
		isEditableSyncLoaded,
		isInlineEditingEnabled,
	}: FirstGroupRowProps) => {
		const cloudId = useCloudId();

		const firstGroupRowData = useFragment<FirstGroupRow$key>(
			graphql`
				fragment FirstGroupRow on JiraSpreadsheetGroup
				@argumentDefinitions(
					isInlineEditingEnabled: { type: "Boolean", defaultValue: true }
					isReparentingEnabled: { type: "Boolean", defaultValue: false }
					isTimelineEnabled: { type: "Boolean!" }
					isDensityFull: { type: "Boolean", defaultValue: false }
					projectKeys: { type: "[String!]" }
					projectKey: { type: "String" }
					viewConfigInput: { type: "JiraIssueSearchViewConfigInput" }
					isConditionalFormattingEnabled: { type: "Boolean!" }
					isLinkRelationshipFieldEnabled: { type: "Boolean!" }
					isRemoteLinkedIssuesEnabled: { type: "Boolean!" }
					isEditableSyncLoaded: { type: "Boolean!" }
				) {
					id
					jql @required(action: THROW)
					fieldId
					fieldType
					fieldSetId
					fieldValue {
						__typename
						jqlTerm
						... on JiraJqlPriorityFieldValue {
							priority {
								id
								priorityId
								name
								iconUrl
							}
						}
						... on JiraJqlUserFieldValue {
							user {
								id
								accountId
								name
								picture
							}
						}
						... on JiraJqlStatusFieldValue {
							status {
								id
								name
								statusCategory {
									id
									statusCategoryId
								}
							}
						}
						... on JiraJqlNumberFieldValue {
							number
						}
						... on JiraJqlOptionFieldValue {
							option {
								id
								optionId
								value
								color {
									colorKey
									id
								}
							}
						}
						... on JiraJqlSprintFieldValue {
							sprint {
								id
								sprintId
								name
								state
								endDate
							}
						}
						... on JiraJqlLabelFieldValue {
							label {
								name
							}
						}
						... on JiraJqlGroupFieldValue {
							group {
								id
								groupId
								name
							}
						}
						... on JiraJqlVersionFieldValue {
							version {
								id
								versionId
								name
							}
						}
					}
					...GroupHeader
					issues(
						viewConfigInput: $viewConfigInput
						fieldSetsInput: $fieldSetsInput
						scope: $fieldSetsScope
						first: 50
						settings: $settings
					) @connection(key: "firstGroupSection_nativeIssueTable__issues") {
						# eslint-disable-next-line @atlassian/relay/unused-fields
						edges {
							__typename
						}
						__id
						# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
						...ChildRowsPagination_issues
							@arguments(
								isInlineEditingEnabled: $isInlineEditingEnabled
								isReparentingEnabled: $isReparentingEnabled
								isTimelineEnabled: $isTimelineEnabled
								isDensityFull: $isDensityFull
								projectKeys: $projectKeys
								projectKey: $projectKey
								shouldQueryHasChildren: false
								isConditionalFormattingEnabled: $isConditionalFormattingEnabled
								isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
								isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled
								isEditableSyncLoaded: $isEditableSyncLoaded
							)
					}
				}
			`,
			firstGroupRow,
		);
		const variables = useMemo(() => {
			return {
				...getChildQueryVariablesForJql<Column>({
					cloudId,
					columns,
					jql: firstGroupRowData.jql,
					isReparentingEnabled: false,
					isTimelineEnabled,
					actionColumns,
					amountOfColumns,
					pageSize,
					isHideDoneItemsEnabled,
					isConditionalFormattingEnabled,
					isInlineFieldConfigEnabled,
					isInlineEditingEnabled,
					isLinkRelationshipFieldEnabled,
					isRemoteLinkedIssuesEnabled,
					isEditableSyncLoaded,
				}),
				shouldQueryFieldSetsById: false,
				namespace: 'ISSUE_NAVIGATOR',
				shouldQueryHasChildren: false,
				isConditionalFormattingEnabled,
			};
		}, [
			cloudId,
			columns,
			firstGroupRowData.jql,
			isTimelineEnabled,
			actionColumns,
			amountOfColumns,
			pageSize,
			isConditionalFormattingEnabled,
			isHideDoneItemsEnabled,
			isInlineFieldConfigEnabled,
			isInlineEditingEnabled,
			isLinkRelationshipFieldEnabled,
			isRemoteLinkedIssuesEnabled,
			isEditableSyncLoaded,
		]);

		const rankFieldData = useFragment(
			graphql`
				fragment FirstGroupRow_rankField on JiraJqlFieldWithAliases {
					...FirstGroupIssues
				}
			`,
			rankField,
		);

		const baseProps = ['cloudId', 'issueSearchInput', 'fieldSetsInput', 'scope'] as const;
		const propsWithViewConfigInput = [...baseProps, 'viewConfigInput'] as const;

		const hasInitialised = useInitialiseFirstGroupIssues({
			groupId: firstGroupRowData.id,
			groupConnectionId,
			firstGroupItemsConnectionId: firstGroupRowData.issues?.__id ?? null,
			issueSearchVariables: pick(
				variables,
				...(isHideDoneItemsEnabled ? propsWithViewConfigInput : baseProps),
			),
			loading,
		});

		const [isExpanded] = useIsItemExpanded({
			itemId: firstGroupRowData.id,
			groupId: firstGroupRowData.id,
			itemsConnectionId: groupConnectionId,
		});

		const inlineCreateHandlers = useInlineCreateHandlers({
			projectKey,
			defaultAssigneeId:
				firstGroupRowData.fieldType === ASSIGNEE_TYPE
					? firstGroupRowData.fieldValue?.user?.id
					: undefined,
			minHierarchyLevel: 0,
		});

		const { fieldId, fieldType } = firstGroupRowData;
		let groupContextFieldInput: GroupContextField | undefined;

		if (fieldId && fieldType) {
			groupContextFieldInput = toGroupContextFieldInput({
				fieldType,
				fieldId,
				fieldValue: firstGroupRowData.fieldValue,
				groupId: firstGroupRowData.id,
				fieldSetId: firstGroupRowData.fieldSetId,
			});
		}

		const renderChildIssueControllerWithGroupContextField: RenderCreateIssueController =
			useCallback(
				(args) => {
					return renderCreateIssueController?.({ ...args, groupContextFieldInput });
				},
				[renderCreateIssueController, groupContextFieldInput],
			);

		const renderRowsWithGroupContextField: RenderRows = useCallback(
			(args) => {
				return renderRows?.({ ...args, groupContextFieldInput });
			},
			[renderRows, groupContextFieldInput],
		);

		return (
			<>
				{isVirtual && (
					<GroupHeader
						projectKey={projectKey}
						groupHeader={firstGroupRowData}
						groupConnectionId={groupConnectionId}
						isGroupAtTop={isGroupAtTop}
						createChildTriggerProps={inlineCreateHandlers?.triggerProps}
						columnsCount={columns.length}
						groupId={firstGroupRowData.id}
						isIssueCreateEnabled={isIssueCreateEnabled}
						isGridNavigationEnabled={isGridNavigationEnabled}
						isStickyColumnsVisible={isStickyColumnsVisible}
						isAutoUnstickyOnSmallScreenEnabled={isAutoUnstickyOnSmallScreenEnabled}
					/>
				)}
				{hasInitialised && isExpanded && (
					<FirstGroupIssues
						variables={variables}
						rankField={rankFieldData ?? null}
						groupId={firstGroupRowData.id}
						groupConnectionId={groupConnectionId}
						groupFieldValue={firstGroupRowData.fieldValue?.jqlTerm ?? null}
						formProps={inlineCreateHandlers?.formProps}
						isFormVisible={inlineCreateHandlers?.isFormVisible}
						pageSize={pageSize}
						width={width}
						isIssueRankDescending={isIssueRankDescending}
						childItemsExperience={childItemsExperience}
						childItemsLoadMoreExperience={childItemsLoadMoreExperience}
						analyticsSource={analyticsSource}
						renderRows={renderRowsWithGroupContextField}
						renderCreateIssueController={renderChildIssueControllerWithGroupContextField}
						renderDraggableWrapper={renderDraggableWrapper}
						ChildIssueCreateRowComponent={ChildIssueCreateRowComponent}
						isTimelineEnabled={isTimelineEnabled}
					/>
				)}
			</>
		);
	},
);

type GroupRowProps = BaseProps & { groupRow: FirstGroupRow_GroupRow$key };

export const GroupRow = memo(
	({
		groupRow,
		groupConnectionId,
		isVirtual,
		projectKey = null,
		columns,
		isIssueCreateEnabled,
		isGridNavigationEnabled,
		isStickyColumnsVisible,
		isAutoUnstickyOnSmallScreenEnabled,
		renderChildRows,
	}: GroupRowProps) => {
		const groupRowData = useFragment<FirstGroupRow_GroupRow$key>(
			graphql`
				fragment FirstGroupRow_GroupRow on JiraSpreadsheetGroup {
					id
					jql @required(action: THROW)
					fieldId
					fieldType
					fieldSetId
					fieldValue {
						__typename
						jqlTerm
						... on JiraJqlPriorityFieldValue {
							priority {
								id
								priorityId
								name
								iconUrl
							}
						}
						... on JiraJqlUserFieldValue {
							user {
								id
								accountId
								name
								picture
							}
						}
						... on JiraJqlStatusFieldValue {
							status {
								id
								name
								statusCategory {
									id
									statusCategoryId
								}
							}
						}
						... on JiraJqlNumberFieldValue {
							number
						}
						... on JiraJqlOptionFieldValue {
							option {
								id
								optionId
								value
								color {
									colorKey
									id
								}
							}
						}
						... on JiraJqlSprintFieldValue {
							sprint {
								id
								sprintId
								name
								state
								endDate
							}
						}
						... on JiraJqlLabelFieldValue {
							label {
								name
							}
						}
						... on JiraJqlGroupFieldValue {
							group {
								id
								groupId
								name
							}
						}
						... on JiraJqlVersionFieldValue {
							version {
								id
								versionId
								name
							}
						}
					}
					...GroupHeader
				}
			`,
			groupRow,
		);

		const inlineCreateHandlers = useInlineCreateHandlers({
			projectKey,
			defaultAssigneeId:
				groupRowData.fieldType === ASSIGNEE_TYPE ? groupRowData.fieldValue?.user?.id : undefined,
			minHierarchyLevel: 0,
		});

		const { fieldId, fieldType } = groupRowData;
		let groupContextField: GroupContextField | undefined;

		if (fieldId && fieldType) {
			groupContextField = toGroupContextFieldInput({
				fieldType,
				fieldId,
				fieldValue: groupRowData.fieldValue,
				groupId: groupRowData.id,
				fieldSetId: groupRowData.fieldSetId,
			});
		}

		return (
			groupRowData && (
				<>
					{isVirtual && (
						<GroupHeader
							projectKey={projectKey}
							groupHeader={groupRowData}
							groupConnectionId={groupConnectionId}
							createChildTriggerProps={inlineCreateHandlers?.triggerProps}
							columnsCount={columns.length}
							groupId={groupRowData.id}
							isIssueCreateEnabled={isIssueCreateEnabled}
							isGridNavigationEnabled={isGridNavigationEnabled}
							isStickyColumnsVisible={isStickyColumnsVisible}
							isAutoUnstickyOnSmallScreenEnabled={isAutoUnstickyOnSmallScreenEnabled}
						/>
					)}
					{renderChildRows?.({
						parentItemId: groupRowData.id,
						itemsConnectionId: groupConnectionId,
						depth: 0,
						type: 'GROUP_CHILDREN',
						groupFieldValue: groupRowData.fieldValue?.jqlTerm ?? null,
						groupJql: groupRowData.jql,
						formProps: inlineCreateHandlers?.formProps,
						isFormVisible: inlineCreateHandlers?.isFormVisible,
						groupId: groupRowData.id,
						groupContextField,
					})}
				</>
			)
		);
	},
);
